import { RefObject, useEffect, useState } from 'react';

// Fallback until the real sticky-header height is measured.
const APP_BAR_HEIGHT = 96;

/**
 * Pins the ref'd element below the app's sticky header (which shrinks as the nav
 * collapses on scroll) via --compare-sticky-top. Returns whether it's pinned.
 */
export function useStickyHeaderOffset(ref: RefObject<HTMLElement>) {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const banner = document.querySelector('.promotionBanner');
    let headerEl: Element | null = banner;
    while (headerEl && window.getComputedStyle(headerEl).position !== 'sticky') {
      headerEl = headerEl.parentElement;
    }

    const handleScroll = () => {
      const node = ref.current;
      if (!node) return;
      const headerBottom = headerEl ? Math.max(0, headerEl.getBoundingClientRect().bottom) : APP_BAR_HEIGHT;
      node.style.setProperty('--compare-sticky-top', `${headerBottom}px`);
      const nextStuck = node.getBoundingClientRect().top <= headerBottom + 1;
      setStuck((prev) => (prev === nextStuck ? prev : nextStuck));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // Header collapses/expands (e.g. on Select focus) without firing scroll/resize;
    // track its live height so the pinned offset can't go stale.
    let observer: ResizeObserver | undefined;
    if (headerEl && typeof window.ResizeObserver !== 'undefined') {
      observer = new window.ResizeObserver(handleScroll);
      observer.observe(headerEl);
    }

    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      observer?.disconnect();
    };
  }, [ref]);

  return stuck;
}
